import { useState } from "react";
import axios from "axios";
import { API } from "../context/AppContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import PageBackdrop from "../components/PageBackdrop";
import { POLICIES } from "../content/policies";

const inp = "w-full bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-4 py-3 text-white font-mono text-sm";
const shell = "bl-page min-h-screen bg-black pt-28 pb-20 px-6";
const wrap = "bl-page-content max-w-3xl mx-auto";

// Supplied reference art per page (portrait art anchors right; landscape art covers the viewport).
const ART = {
  science: "/assets/bg/science.webp",
  testing: "/assets/bg/testing.webp",
  about: "/assets/bg/about.webp",
  shipping: "/assets/bg/shipping.webp",
  faq: "/assets/bg/faq.webp",
  contact: "/assets/bg/contact.webp",
  privacy: "/assets/bg/privacy.webp",
  terms: "/assets/bg/terms.webp",
  returns: "/assets/bg/terms.webp",
  disclaimer: "/assets/bg/disclaimer.webp",
};

export function Science() {
  return (
    <div className={shell}><PageBackdrop image={ART.science} dim={0.5} /><div className={wrap}>
      <div className="bl-eyebrow text-[color:var(--bl-blue)]">/ Science</div>
      <h1 className="bl-heading text-white text-5xl md:text-6xl mt-4">Precision at every stage.</h1>
      <div className="mt-10 space-y-8 text-white/70 leading-relaxed">
        <p>Peptide science demands rigor at every step. We select suppliers, define specifications and pair each production run with independent analytical testing. Where a certificate has not yet cleared review, we say so.</p>
        <p>The four pillars — <span className="text-white">Identity</span>, <span className="text-white">Purity</span>, <span className="text-white">Consistency</span> and <span className="text-white">Traceability</span> — govern every release. Nothing is inferred. Nothing is fabricated.</p>
        <p>This is a research supply. Products on this catalogue are not approved medicines and are not offered for therapeutic use.</p>
      </div>
    </div></div>
  );
}

export function Testing() {
  return (
    <div className={shell}><PageBackdrop image={ART.testing} dim={0.55} /><div className="bl-page-content max-w-[1100px] mx-auto">
      <div className="bl-eyebrow text-[color:var(--bl-gold)]">/ Testing</div>
      <h1 className="bl-heading text-white text-5xl md:text-6xl mt-4">Quality you can trace.</h1>
      <p className="text-white/60 mt-6 max-w-2xl">Every batch is designed to be paired with independent testing. Where a COA is not yet posted, we mark the product as pending. We do not fabricate purity results or certificates.</p>
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {[
          ["Batch Identification", "Every production run is assigned a batch/lot that travels with the product from release to receipt."],
          ["Independent Analysis", "Third-party laboratories conduct identity and purity assays on each batch."],
          ["Certificate of Analysis", "COAs are attached to products once test data has cleared review and release."],
          ["Chain of Custody", "Traceability is maintained from supplier through packaging to dispatch."],
        ].map(([k, v]) => (
          <div key={k} className="bl-glass border border-white/5 p-8">
            <div className="bl-eyebrow text-[color:var(--bl-blue)]">{k}</div>
            <p className="text-white/70 mt-4 leading-relaxed">{v}</p>
          </div>
        ))}
      </div>
    </div></div>
  );
}

export function About() {
  return (
    <div className={shell}><PageBackdrop image={ART.about} dim={0.45} /><div className={wrap}>
      <div className="bl-eyebrow">/ About</div>
      <h1 className="bl-heading text-white text-5xl md:text-6xl mt-4">Regeneration Bio Labz.</h1>
      <div className="mt-10 space-y-8 text-white/70 leading-relaxed">
        <p>Bio Labz was founded on a straightforward principle — treat research supply with the seriousness it deserves. That means specifications, testing, traceability and honest communication.</p>
        <p>We do not chase supplement-industry hype. We do not fabricate claims. We supply compounds intended for laboratory research and pair them with the analytical framework the field expects.</p>
        <p className="text-white">Peptide science. Human potential. Built on science. Delivered with trust.</p>
      </div>
    </div></div>
  );
}

export function FAQPage() {
  const FAQS = [
    ["What is a research compound?", "A material intended for laboratory investigation only. Not for human or veterinary use."],
    ["Do you provide dosing protocols?", "No. We do not provide dosing guidance for the compounds on this site."],
    ["Do you ship internationally?", "Please see the Shipping page for current serviced regions and lead times."],
    ["How long does delivery take in Australia?", "4–7 days from payment confirmation, dispatched from Australia with tracking."],
    ["How can I pay?", "Cryptocurrency only — Bitcoin (BTC) or USDT (USD stablecoin). We do not take card or bank payments. Prices are shown in A$; your order is dispatched once the payment confirms on-chain."],
    ["How do returns work?", "See the Returns policy for detail on eligibility and process."],
    ["Where are your products manufactured?", "Our suppliers are selected on a specifications and testing basis. Batch-level detail is captured in COAs where available."],
    ["When is a COA published?", "Once independent analytical review has cleared release for a specific batch."],
  ];
  return (
    <div className={shell}><PageBackdrop image={ART.faq} align="cover" dim={0.55} /><div className={wrap}>
      <div className="bl-eyebrow">/ FAQ</div>
      <h1 className="bl-heading text-white text-5xl md:text-6xl mt-4">Questions.</h1>
      <div className="mt-12 divide-y divide-white/5">
        {FAQS.map(([q,a])=> (
          <div key={q} className="py-8">
            <div className="font-display uppercase text-white text-xl">{q}</div>
            <p className="text-white/60 mt-3 leading-relaxed">{a}</p>
          </div>
        ))}
      </div>
    </div></div>
  );
}

export function Contact() {
  const [name,setName]=useState(""); const [email,setEmail]=useState(""); const [msg,setMsg]=useState(""); const [sending,setSending]=useState(false);
  const onSubmit=async(e)=>{
    e.preventDefault(); setSending(true);
    try { await axios.post(`${API}/contact`,{name,email,message:msg}); toast.success("Message received"); setName("");setEmail("");setMsg(""); }
    catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };
  return (
    <div className={shell}><PageBackdrop image={ART.contact} align="cover" dim={0.55} /><div className={wrap}>
      <div className="bl-eyebrow">/ Contact</div>
      <h1 className="bl-heading text-white text-5xl md:text-6xl mt-4">Get in touch.</h1>
      <p className="text-white/60 mt-4 max-w-lg">For research supply inquiries, testing questions or partnership discussions.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-xl" data-testid="contact-mailboxes">
        {[["Support & orders", "support@regenbiolabz.com"], ["Sales & availability", "sales@regenbiolabz.com"]].map(([l, e]) => (
          <a key={e} href={`mailto:${e}`} data-testid={`contact-mail-${l.split(" ")[0].toLowerCase()}`} className="border border-white/10 hover:border-[color:var(--bl-blue)]/60 px-4 py-3 transition-colors">
            <div className="bl-eyebrow text-white/40">{l}</div>
            <div className="font-mono text-xs text-white mt-1 break-all">{e}</div>
          </a>
        ))}
      </div>
      <form onSubmit={onSubmit} className="mt-10 space-y-4 max-w-xl">
        <input data-testid="contact-name" value={name} onChange={e=>setName(e.target.value)} placeholder="Name" required className={inp} />
        <input data-testid="contact-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" required className={inp} />
        <textarea data-testid="contact-message" value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Message" required rows={6} className={inp} />
        <button data-testid="contact-submit" disabled={sending} className="bl-btn primary disabled:opacity-60">{sending?"Sending...":"Send Message"}</button>
      </form>
    </div></div>
  );
}

const legal = {
  privacy: { title: "Privacy Policy", body: "We collect the minimum data required to process orders and operate the Inner Circle member area. Personal data is not sold. Contact us to request access, correction or deletion." },
  terms: { title: "Terms of Service", body: "By purchasing from Bio Labz you confirm that products will be used for laboratory research and not for human or veterinary consumption. All local import and research regulations are the buyer's responsibility." },
  "shipping-policy": { title: "Shipping Policy", body: "" },
  shipping: { title: "Shipping", body: "Orders are dispatched from Australia. Delivery within Australia takes 4–7 days from payment confirmation. We accept Bitcoin and USDT only (no cards or bank payments). Import compliance is the buyer's responsibility." },
  returns: { title: "Returns", body: "Sealed unopened items may be eligible for return within 14 days. Peptide vials, once opened, are not eligible for return due to product integrity requirements." },
  "age-verification": { title: "Age Verification", body: "Regeneration Bio Labz sells research compounds to adults only. You must be at least 18 years of age to browse, create an account or place an order. On your first visit you are asked to confirm your age and that you are purchasing for laboratory research; your confirmation is stored on your device only (it is not linked to your identity). We may request proof of age before releasing an order and reserve the right to cancel any order where age cannot be verified. Products are not for human or veterinary consumption." },
  cookies: { title: "Cookie Policy", body: "We use only the cookies and browser storage needed to run the site:\n\n• A secure, httpOnly session cookie when you sign in to the Inner Circle member area (expires after 30 days or when you sign out; a password reset signs out all devices).\n• A separate httpOnly session cookie for the store owner's admin area.\n• Local storage on your device for your cart, your age/research-use confirmation and whether you have seen the welcome offer.\n• Session storage (cleared when the tab closes) for the support-chat conversation and intro film.\n\nWe do not use advertising, cross-site tracking or third-party analytics cookies. Payments are processed as cryptocurrency invoices; no card data is ever stored. You can clear cookies and site data at any time in your browser settings — you will simply be asked to sign in and confirm your age again." },
  disclaimer: { title: "Research Disclaimer", body: "Products supplied by Regeneration Bio Labz are intended solely for laboratory research use. They are not approved medicines and are not offered for human or veterinary consumption. Nothing on this site should be interpreted as medical advice, diagnosis or treatment. We do not provide dosing protocols. Where analytical data is not yet available, it is marked as pending — we do not fabricate laboratory results." },
};
const POLICY_LINKS = [
  ["Terms and Conditions", "/legal/terms"],
  ["Returns and Refunds Policy", "/legal/returns"],
  ["Privacy Policy", "/legal/privacy"],
  ["Contact page", "/contact"],
  ["support@regenbiolabz.com", "mailto:support@regenbiolabz.com"],
];
const LINK_RE = new RegExp(`(${POLICY_LINKS.map(([t]) => t).join("|")})`, "g");

function linkify(text) {
  return text.split(LINK_RE).map((part, i) => {
    const hit = POLICY_LINKS.find(([t]) => t === part);
    if (!hit) return part;
    const cls = "text-[color:var(--bl-blue)] underline decoration-[color:var(--bl-blue)]/40 underline-offset-4 hover:decoration-[color:var(--bl-blue)]";
    if (hit[1].startsWith("mailto:")) return <a key={`${part}-${i}`} href={hit[1]} data-testid="policy-link-email" className={cls}>{part}</a>;
    return <Link key={`${part}-${i}`} to={hit[1]} data-testid={`policy-link-${hit[1].split("/").pop()}`} className={cls}>{part}</Link>;
  });
}

function PolicyBody({ policy }) {
  return (
    <div className="mt-10" data-testid="policy-body">
      <div className="bl-eyebrow text-[color:var(--bl-gold)]">{policy.title} · {policy.scope || "Australia"} · Effective {policy.last_updated}</div>
      {policy.intro && <p className="mt-6 text-white/70 leading-relaxed">{policy.intro}</p>}
      {policy.sections.map((sec) => (
        <section key={sec.heading} className="mt-10" data-testid={`policy-section-${sec.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <h2 className="font-display text-white text-lg tracking-wide">{sec.heading}</h2>
          <div className="mt-3 space-y-3 text-white/70 leading-relaxed">
            {sec.body.map((b) => b.type === "bullet"
              ? <div key={b.text} className="flex gap-3"><span className="text-[color:var(--bl-blue)] mt-2 h-1 w-1 rounded-full bg-current shrink-0" /><p>{linkify(b.text)}</p></div>
              : <p key={b.text}>{linkify(b.text)}</p>)}
          </div>
        </section>
      ))}
      {policy.footer && <div className="mt-12 pt-6 border-t border-white/8 font-mono text-[10px] tracking-[0.2em] uppercase text-white/35">{policy.footer}</div>}
    </div>
  );
}

export function Legal({ slug }) {
  const l = legal[slug];
  if (!l) return <div className={shell}><div className={wrap}><h1 className="bl-heading text-white text-3xl">Not Found</h1></div></div>;
  const backdrop = slug === "shipping" || slug === "shipping-policy"
    ? <PageBackdrop image={ART.shipping} dim={0.5} />
    : <PageBackdrop image={ART[slug]} align="cover" dim={0.6} />;
  return (
    <div className={shell}>{backdrop}<div className={wrap}>
      <div className="bl-eyebrow">/ Legal</div>
      <h1 className="bl-heading text-white text-5xl md:text-6xl mt-4">{POLICIES[slug]?.title || l.title}</h1>
      {POLICIES[slug] ? <PolicyBody policy={POLICIES[slug]} /> : (
        <div className="mt-10 text-white/70 leading-relaxed whitespace-pre-line">{l.body}</div>
      )}
      <div className="mt-10"><Link to="/" className="bl-btn">Back Home</Link></div>
    </div></div>
  );
}
